import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';
import { AgentGroupsList } from './AgentGroupsList';
import { AgentGroupForm } from './AgentGroupForm';
import { AgentGroupDetail } from './AgentGroupDetail';
import {
  getAgentGroups,
  getAgentGroupById,
  createAgentGroup,
  updateAgentGroup,
  deleteAgentGroup,
  addGroupMembers,
  removeGroupMember,
} from '../../../../actions/agentGroups';
import { useToken } from '../../../../hooks/useToken';

const LIMIT = 50;

export const AgentGroupsSection = ({ canManage }) => {
  const agent = useSelector((state) => state.agent);
  const { agents } = useSelector((state) => state.agentes);
  const { getTokenSilent } = useToken();

  const [groups, setGroups] = useState([]);
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [groupToEdit, setGroupToEdit] = useState(null);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailGroup, setDetailGroup] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, severity: 'success', message: '' });

  const notify = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, severity, message });
  }, []);

  const loadGroups = useCallback(async (currentSkip = 0, append = false) => {
    if (!agent.id_customer) return;
    setLoading(true);
    try {
      const token = await getTokenSilent();
      const data = await getAgentGroups({ id_customer: agent.id_customer, token, skip: currentSkip, limit: LIMIT });
      setGroups((prev) => append ? [...prev, ...data] : data);
      setHasMore(data.length === LIMIT);
      setSkip(currentSkip);
    } catch {
      notify('Error al cargar grupos', 'error');
    } finally {
      setLoading(false);
    }
  }, [agent.id_customer, getTokenSilent, notify]);

  useEffect(() => {
    loadGroups(0);
  }, [loadGroups]);

  const handleLoadMore = () => {
    loadGroups(skip + LIMIT, true);
  };

  const handleOpenCreate = () => {
    setGroupToEdit(null);
    setFormOpen(true);
  };

  const handleOpenEdit = (group) => {
    setGroupToEdit(group);
    setFormOpen(true);
  };

  const handleFormSave = async ({ name, description, group: editingGroup }) => {
    const token = await getTokenSilent();
    try {
      if (editingGroup) {
        const body = {};
        if (name !== editingGroup.name) body.name = name;
        if (description !== (editingGroup.description || '')) body.description = description;
        await updateAgentGroup({ group_id: editingGroup.id, ...body, token });
        notify('Grupo actualizado');
      } else {
        await createAgentGroup({ name, description, id_customer: agent.id_customer, token });
        notify('Grupo creado');
      }
      setFormOpen(false);
      loadGroups(0);
    } catch {
      notify('Error al guardar grupo', 'error');
      throw new Error('save failed');
    }
  };

  const handleOpenDetail = async (group) => {
    setDetailGroup(group);
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const token = await getTokenSilent();
      const detail = await getAgentGroupById({ group_id: group.id, token });
      setDetailGroup(detail);
    } catch {
      notify('Error al cargar detalle del grupo', 'error');
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshDetail = async (group_id) => {
    const token = await getTokenSilent();
    const detail = await getAgentGroupById({ group_id, token });
    setDetailGroup(detail);
  };

  const handleUpdateMembers = async (newGroupAgents) => {
    const token = await getTokenSilent();
    const originalIds = new Set((detailGroup.members || []).map((m) => m.agent_id));
    const newIds = new Set(newGroupAgents.map((a) => a._id));

    const toAdd = newGroupAgents.filter((a) => !originalIds.has(a._id)).map((a) => a._id);
    const toRemove = [...originalIds].filter((id) => !newIds.has(id));

    if (toAdd.length === 0 && toRemove.length === 0) return;

    setUpdateLoading(true);
    try {
      await Promise.all([
        toAdd.length > 0
          ? addGroupMembers({ group_id: detailGroup.id, agent_ids: toAdd, token })
          : Promise.resolve(),
        ...toRemove.map((agent_id) =>
          removeGroupMember({ group_id: detailGroup.id, agent_id, token })
        ),
      ]);
      await refreshDetail(detailGroup.id);
      notify('Grupo actualizado');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Error al actualizar miembros';
      notify(msg, 'error');
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleOpenDelete = (group) => {
    setGroupToDelete(group);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    setDeleteLoading(true);
    try {
      const token = await getTokenSilent();
      await deleteAgentGroup({ group_id: groupToDelete.id, token });
      notify('Grupo eliminado');
      setDeleteDialogOpen(false);
      setGroupToDelete(null);
      loadGroups(0);
    } catch {
      notify('Error al eliminar grupo', 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Box>
      <AgentGroupsList
        groups={groups}
        loading={loading}
        hasMore={hasMore}
        onLoadMore={handleLoadMore}
        onCreateGroup={handleOpenCreate}
        onEditGroup={handleOpenEdit}
        onDeleteGroup={handleOpenDelete}
        onViewDetail={handleOpenDetail}
        canManage={canManage}
      />

      <AgentGroupForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={handleFormSave}
        group={groupToEdit}
      />

      <AgentGroupDetail
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        group={detailGroup}
        agents={agents?.items || []}
        loading={detailLoading}
        onUpdate={handleUpdateMembers}
        updateLoading={updateLoading}
        canManage={canManage}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleteLoading && setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Eliminar grupo</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Eliminar el grupo <strong>{groupToDelete?.name}</strong>? Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={deleteLoading}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
